import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { CalendarDaysIcon, PlusIcon } from '@heroicons/react/24/outline';
import { slideUp } from '@/lib/motion';
import PageHeader from '@/components/common/PageHeader';
import FeatureUpgradeNotice from '@/components/common/FeatureUpgradeNotice';
import { usePlanFeatures } from '../../hooks/usePlanFeatures';
import CalendarToolbar, {
  type CalendarView,
} from '../../components/calendar/CalendarToolbar';
import CalendarMonthView from '../../components/calendar/CalendarMonthView';
import CalendarWeekView from '../../components/calendar/CalendarWeekView';
import CalendarDayView from '../../components/calendar/CalendarDayView';
import CalendarAgendaView from '../../components/calendar/CalendarAgendaView';
import ConnectGoogleEmptyState from '../../components/calendar/ConnectGoogleEmptyState';
import ReconnectBanner from '../../components/calendar/ReconnectBanner';
import EventModal, {
  type EventModalMode,
} from '../../components/calendar/EventModal';
import {
  useIntegrations,
  useStartGoogleConnect,
} from '../../hooks/useIntegrations';
import { useCalendarEvents } from '../../hooks/useCalendarEvents';
import {
  getViewRange,
  navigateCursor,
} from '../../components/calendar/calendarDateUtils';
import type { CalendarEvent } from '../../types/integrations';

interface ModalState {
  mode: EventModalMode;
  event?: CalendarEvent;
  initialDate?: Date;
}

const ERROR_REASON_COPY: Record<string, string> = {
  invalid_state: 'Connection failed — please try again.',
  user_mismatch:
    'You appear to have switched accounts. Sign back in and try again.',
  token_exchange_failed: 'Google rejected the connection. Please try again.',
  missing_account_email: 'Could not read your Google account. Please try again.',
  access_denied: 'Connection was canceled.',
};

const CalendarPage: React.FC = () => {
  const { calendar: calendarAllowed, plan } = usePlanFeatures();
  const [view, setView] = useState<CalendarView>('month');
  const [cursorDate, setCursorDate] = useState<Date>(new Date());
  const [modal, setModal] = useState<ModalState | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Handle OAuth callback fallback (in case backend redirects here directly)
  useEffect(() => {
    const status = searchParams.get('status');
    if (!status) return;
    const reason = searchParams.get('reason');
    const accountEmail = searchParams.get('account_email');
    if (status === 'success') {
      toast.success(
        accountEmail
          ? `Google Calendar connected — ${accountEmail}`
          : 'Google Calendar connected'
      );
    } else if (status === 'error') {
      toast.error(
        ERROR_REASON_COPY[reason ?? ''] || 'Connection failed — please try again.'
      );
    }
    const next = new URLSearchParams(searchParams);
    next.delete('status');
    next.delete('provider');
    next.delete('account_email');
    next.delete('reason');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const { data: connections = [], isLoading: connectionsLoading } = useIntegrations();
  const startConnect = useStartGoogleConnect();
  const googleConnection = useMemo(
    () => connections.find((c) => c.provider === 'google') ?? null,
    [connections]
  );

  const isConnected = googleConnection?.status === 'active';
  const needsReauth =
    !!googleConnection &&
    (googleConnection.status === 'reauth_required' ||
      googleConnection.status === 'expired' ||
      googleConnection.status === 'revoked');

  const { from, to } = useMemo(() => getViewRange(view, cursorDate), [view, cursorDate]);
  const {
    data: events = [],
    isLoading: eventsLoading,
  } = useCalendarEvents({ from, to, enabled: isConnected });

  const handleNavigate = (delta: -1 | 0 | 1) => {
    setCursorDate((prev) => navigateCursor(view, prev, delta));
  };

  const handleEventClick = (event: CalendarEvent) => {
    // Always open in view mode. The modal fetches full detail, which tells us
    // whether the event is a Tereiko ad_hoc (and therefore editable). Promoting
    // to edit happens from inside the modal once that's known.
    setModal({ mode: 'view', event });
  };

  const handleDayClick = (date: Date) => {
    if (!isConnected) return;
    setModal({ mode: 'create', initialDate: date });
  };

  const handleAddEvent = () => {
    if (!isConnected) return;
    setModal({ mode: 'create', initialDate: cursorDate });
  };

  const renderView = () => {
    if (!isConnected) return null;
    const props = { events, onEventClick: handleEventClick, onDayClick: handleDayClick };
    switch (view) {
      case 'month':
        return <CalendarMonthView cursorDate={cursorDate} {...props} />;
      case 'week':
        return <CalendarWeekView cursorDate={cursorDate} {...props} />;
      case 'day':
        return <CalendarDayView cursorDate={cursorDate} {...props} />;
      case 'agenda':
        return (
          <CalendarAgendaView
            cursorDate={cursorDate}
            events={events}
            onEventClick={handleEventClick}
          />
        );
      default:
        return null;
    }
  };

  // Staff surface: locked plan features are shown with an upgrade notice.
  if (!calendarAllowed) {
    return (
      <motion.div {...slideUp}>
        <PageHeader title="Calendar" className="mb-6" />
        <FeatureUpgradeNotice
          feature="Calendar"
          description="Plan sessions on a shared calendar and sync events with Google Calendar."
          currentPlan={plan}
          icon={<CalendarDaysIcon className="w-8 h-8" />}
        />
      </motion.div>
    );
  }

  return (
    <motion.div {...slideUp}>
      <PageHeader
        title="Calendar"
        className="mb-6"
        actions={
          <button
            type="button"
            onClick={handleAddEvent}
            disabled={!isConnected}
            className="btn btn-primary rounded-full inline-flex items-center gap-2 active:scale-[0.97] transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <PlusIcon className="w-4 h-4" />
            Add event
          </button>
        }
      />

      {/* States */}
      {connectionsLoading ? (
        <div className="card-elevated p-12 text-center text-text-secondary">
          Loading…
        </div>
      ) : !googleConnection ? (
        <ConnectGoogleEmptyState startConnect={startConnect} />
      ) : (
        <div className="space-y-4">
          {needsReauth && (
            <ReconnectBanner
              status={googleConnection.status}
              startConnect={startConnect}
            />
          )}
          <div
            className={[
              'card-elevated overflow-hidden',
              needsReauth ? 'opacity-60 pointer-events-none' : '',
            ].join(' ')}
          >
            <CalendarToolbar
              view={view}
              onViewChange={setView}
              cursorDate={cursorDate}
              onNavigate={handleNavigate}
              onTodayClick={() => setCursorDate(new Date())}
              disabled={needsReauth}
            />
            <div className="relative min-h-[600px]">
              {eventsLoading && (
                <div className="absolute top-2 right-2 text-xs text-text-tertiary">
                  Refreshing…
                </div>
              )}
              {renderView()}
            </div>
          </div>
        </div>
      )}

      {modal && (
        <EventModal
          mode={modal.mode}
          event={modal.event}
          initialDate={modal.initialDate}
          onClose={() => setModal(null)}
        />
      )}
    </motion.div>
  );
};

export default CalendarPage;
